import { useEffect, useMemo, useState } from "react";
import { Activity, BarChart3, BellRing, Bot, Check, ChevronDown, ChevronRight, Globe, MapPin, Pencil, Undo2, Ban, Unlock, X, Zap } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ExpandableText from "./ExpandableText";

// [jours, décalage (0 = se termine aujourd'hui, 1 = hier), libellé]
const RANGES = [[1, 0, "Aujourd'hui"], [1, 1, "Hier"], [7, 0, "7 j"], [15, 0, "15 j"], [30, 0, "30 j"], [90, 0, "90 j"], [180, 0, "6 mois"], [365, 0, "1 an"]];
const periodLabel = (r, o) =>
  o === 1 && r === 1 ? "hier" : r === 1 ? "aujourd'hui" : r === 365 ? "1 an" : r === 180 ? "6 mois" : `${r} derniers jours`;

// Libellé court de date pour l'axe du graphique (« 5 sept. »)
const dayLabel = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "short" });

// Carte « Visiteurs en ligne » de l'aperçu admin : nombre de visiteurs actifs
// (signal de présence des 5 dernières minutes), pages consultées, et graphique
// journalier des pages vues (aujourd'hui, hier, 7 à 365 jours). Chaque journée est un
// compteur à part : les journées précédentes sont conservées (collection analytics).
export default function LiveVisitors() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [range, setRange] = useState(30);
  const [offset, setOffset] = useState(0);      // 1 = « Hier »
  const [daily, setDaily] = useState(null);
  const [hotPages, setHotPages] = useState(null); // « Pages chaudes » : période INDÉPENDANTE du graphique
  const [hotRange, setHotRange] = useState(30);
  const [hotOffset, setHotOffset] = useState(0);
  const [openIdx, setOpenIdx] = useState(null);   // visiteur dont le parcours est déplié
  const [hist, setHist] = useState(null);         // historique des visiteurs (période choisie)
  const [histGeo, setHistGeo] = useState({});     // {ip: {label: "Ville, Province", bot: bool}}
  const [histBots, setHistBots] = useState(0);    // robots masqués de la liste (Google, Bing…)
  const [histBotsList, setHistBotsList] = useState([]); // lignes des robots (section « IP robots »)
  const [histBotsOpen, setHistBotsOpen] = useState(false); // section robots dépliée
  const [histOpen, setHistOpen] = useState(null); // IP de l'historique dépliée
  const [histRange, setHistRange] = useState(30); // période propre à l'historique
  const [histOffset, setHistOffset] = useState(0); // 1 = « Hier »
  const [histSkip, setHistSkip] = useState(0);    // pagination : 25 adresses IP par page
  const [histTotal, setHistTotal] = useState(0);  // total d'IP visiteurs sur la période
  const [histHasMore, setHistHasMore] = useState(false);
  const [histLoadingMore, setHistLoadingMore] = useState(false);
  const [hotEmail, setHotEmail] = useState(null); // courriel quotidien Pages chaudes

  // « 5 sept., 14 h 32 » — dernier passage d'un visiteur de l'historique
  const dtshort = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleString("fr-CA", { timeZone: "America/Montreal", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/presence")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let alive = true;
    setDaily(null);
    api.get("/admin/analytics", { params: { days: range, offset } })
      .then((r) => { if (alive) setDaily(r.data); })
      .catch(() => { if (alive) setDaily(false); });
    return () => { alive = false; };
  }, [range, offset]);

  // « Pages chaudes » : son PROPRE sélecteur de période — changer un bouton
  // n'affecte que sa section, jamais le graphique ni le reste de la page
  useEffect(() => {
    let alive = true;
    setHotPages(null);
    api.get("/admin/analytics", { params: { days: hotRange, offset: hotOffset } })
      .then((r) => { if (alive) setHotPages(r.data.top_pages || []); })
      .catch(() => { if (alive) setHotPages([]); });
    return () => { alive = false; };
  }, [hotRange, hotOffset]);

  // Préférence du courriel quotidien « Pages chaudes »
  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => setHotEmail(!!r.data?.hotpages_daily_email))
      .catch(() => setHotEmail(false));
  }, []);

  // Historique par adresse IP (pages parcourues + ville) — 25 IP par page,
  // bouton « Afficher plus » pour charger la suite (skip 25, 50…)
  const loadHist = (skip, append) => {
    if (append) setHistLoadingMore(true);
    return api.get("/admin/analytics/visitors", { params: { days: histRange, offset: histOffset, skip } })
      .then((r) => {
        setHist((h) => (append ? [...(h || []), ...(r.data.visitors || [])] : (r.data.visitors || [])));
        setHistGeo((g) => ({ ...(append ? g : {}), ...(r.data.geo || {}) }));
        setHistBots(r.data.bots_total ?? r.data.bots_hidden ?? 0);
        setHistBotsList(r.data.bots || []);
        setHistBotsOpen(false);
        setBlockedIps(new Set(r.data.blocked_ips || []));
        if (r.data.noms) setIpNames((m) => ({ ...m, ...r.data.noms }));
        setHistTotal(r.data.total || 0);
        setHistHasMore(!!r.data.has_more);
        setHistSkip(skip);
      })
      .catch(() => { if (!append) setHist([]); })
      .finally(() => setHistLoadingMore(false));
  };

  useEffect(() => {
    setHist(null);
    setHistOpen(null);
    loadHist(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [histRange, histOffset]);

  // Classement manuel d'une IP : « Robot » (exclue de toutes les statistiques)
  // ou retour parmi les visiteurs — prioritaire sur la détection automatique
  const markBot = async (ip, bot) => {
    try {
      await api.post("/admin/analytics/visitors/bot", { ip, bot });
      toast.success(bot
        ? `${ip} classée parmi les robots — exclue de toutes les statistiques de visite.`
        : `${ip} replacée parmi les visiteurs.`);
      setHist(null);
      setHistOpen(null);
      loadHist(0, false);
    } catch {
      toast.error("Réglage non enregistré — réessayez.");
    }
  };

  // Bloquage site complet d'une IP (demande du propriétaire, 2026-10-16) —
  // bouton « Bloquer » de l'historique ; réservé au propriétaire.
  // Liste complète des IP bloquées (avec date et auteur) + déblocage (2026-10-17).
  const [blockedIps, setBlockedIps] = useState(new Set());
  const [blockedList, setBlockedList] = useState(null); // [{ip, at, par, nom}]
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [journal, setJournal] = useState(null);      // journal des blocages auto (null = pas chargé)
  const [journalOpen, setJournalOpen] = useState(false);
  const [clics, setClics] = useState(null);          // clics « Soumission 30 s » par endroit (30 j)

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/analytics/clics-soumission")
      .then((r) => setClics(r.data))
      .catch(() => setClics({ par_source: [], total: 0 }));
  }, [isAdmin]);
  const [ipNames, setIpNames] = useState({});           // {ip: nom personnalisé}
  const [editingIp, setEditingIp] = useState(null);     // IP dont on édite le nom
  const [editingVal, setEditingVal] = useState("");
  const [blockMenuIp, setBlockMenuIp] = useState(null); // IP dont le menu de durée est ouvert

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/visiteurs/blocage")
      .then((r) => {
        setBlockedList(r.data.ips || []);
        setIpNames((m) => ({ ...m, ...Object.fromEntries((r.data.ips || []).filter((x) => x.nom).map((x) => [x.ip, x.nom])) }));
      })
      .catch(() => setBlockedList([]));
  }, [isAdmin]);

  // Pose/modifie le nom d'une IP (nom vide = retirer) — historique + bloquées
  const saveNom = async (ip) => {
    const nom = editingVal.trim();
    try {
      await api.post("/admin/analytics/visitors/nom", { ip, nom });
      setIpNames((m) => {
        const n = { ...m };
        nom ? (n[ip] = nom) : delete n[ip];
        return n;
      });
      setBlockedList((l) => l ? l.map((x) => (x.ip === ip ? { ...x, nom } : x)) : l);
      setEditingIp(null);
      toast.success(nom ? `${ip} s'affiche maintenant comme « ${nom} ».` : `Nom retiré de ${ip}.`);
    } catch {
      toast.error("Nom non enregistré — réessayez.");
    }
  };

  const toggleBlock = async (ip, bloque, duree = "toujours") => {
    try {
      if (bloque) await api.post("/admin/visiteurs/blocage", { ip, duree });
      else await api.delete(`/admin/visiteurs/blocage/${encodeURIComponent(ip)}`);
      setBlockedIps((s) => {
        const n = new Set(s);
        bloque ? n.add(ip) : n.delete(ip);
        return n;
      });
      const expire = bloque && duree !== "toujours"
        ? new Date(Date.now() + (duree === "24h" ? 24 : 7 * 24) * 3600 * 1000).toISOString()
        : "";
      setBlockedList((l) => {
        if (!l) return l;
        return bloque
          ? [{ ip, at: new Date().toISOString(), par: user?.email || "", nom: ipNames[ip] || "",
               duree, expire, auto: false }, ...l.filter((x) => x.ip !== ip)]
          : l.filter((x) => x.ip !== ip);
      });
      setBlockMenuIp(null);
      toast.success(bloque
        ? duree === "toujours"
          ? `${ip} bloquée — cette adresse n'a plus accès au site du tout.`
          : `${ip} bloquée pour ${duree === "24h" ? "24 h" : "7 jours"} — le blocage se lèvera tout seul.`
        : `${ip} débloquée — l'accès est rétabli.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Réglage non enregistré — réessayez.");
    }
  };

  const toggleHotEmail = async () => {
    const next = !hotEmail;
    setHotEmail(next);
    try {
      await api.patch("/admin/settings", { hotpages_daily_email: next });
      toast.success(next
        ? "Courriel quotidien activé — chaque matin vers 6 h, le classement des pages d'hier dans votre boîte."
        : "Courriel quotidien désactivé.");
    } catch {
      setHotEmail(!next);
      toast.error("Réglage non enregistré — réessayez.");
    }
  };

  // Toutes les journées de la période, y compris celles sans visite (0)
  const chart = useMemo(() => {
    if (!daily) return [];
    const counts = Object.fromEntries((daily.per_day || []).map((d) => [d.day, d.n]));
    const out = [];
    const today = new Date();
    today.setDate(today.getDate() - offset);  // « Hier » : la fenêtre se termine hier
    for (let i = range - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      // Date LOCALE (pas toISOString = UTC) : sinon le soir venu, le dernier bâton
      // affiche déjà « demain » alors qu'on est encore aujourd'hui à Montréal
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      out.push({ day: iso, label: dayLabel(iso), vues: counts[iso] || 0 });
    }
    return out;
  }, [daily, range, offset]);

  // Chiffre vedette = TOTAL de la période choisie (Aujourd'hui, Hier, 7 j… 1 an) —
  // il suit le bouton sélectionné
  const periodViews = chart.reduce((s, d) => s + d.vues, 0);
  const pl = periodLabel(range, offset);
  const periodTitle = pl.charAt(0).toUpperCase() + pl.slice(1);
  const tickEvery = range > 180 ? 30 : range > 90 ? 15 : range > 30 ? 9 : range > 14 ? 3 : range > 7 ? 2 : 1;

  if (!data) return null;
  const when = (s) => (s < 60 ? `il y a ${s} s` : `il y a ${Math.floor(s / 60)} min`);
  const hhmm = (iso) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("fr-CA", { timeZone: "America/Montreal", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="bg-surface border border-gold/15 rounded-xl p-7 mb-8" data-testid="live-visitors">
      <div className="flex items-center gap-3 mb-4">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400" />
        </span>
        <h3 className="text-xs uppercase tracking-widest text-ivory/70">
          Visiteurs en ligne — <strong className="text-gold" data-testid="live-visitor-count">{data.count}</strong>
        </h3>
        <Activity className="w-4 h-4 text-gold/60 ml-auto" />
      </div>
      {data.visitors.length === 0 ? (
        <p className="text-sm text-ivory/60" data-testid="live-visitors-empty">Personne sur le site en ce moment.</p>
      ) : (
        <ul className="space-y-2">
          {data.visitors.map((v, i) => {
            const open = openIdx === i;
            return (
              <li key={i} data-testid={`live-visitor-${i}`}
                className="text-sm border-b border-ivory/5 pb-2 last:border-0 last:pb-0">
                <button type="button" onClick={() => setOpenIdx(open ? null : i)}
                  data-testid={`live-visitor-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-4 text-left group">
                  <span className="flex items-center gap-2.5 min-w-0 flex-wrap">
                    {open
                      ? <ChevronDown className="w-3.5 h-3.5 text-gold shrink-0" />
                      : <ChevronRight className="w-3.5 h-3.5 text-ivory/40 group-hover:text-gold shrink-0 transition-colors" />}
                    {v.name ? (
                      <span className="shrink-0 text-xs font-semibold text-gold bg-gold/10 border border-gold/30 rounded-full px-2.5 py-0.5"
                        data-testid={`live-visitor-name-${i}`}>{v.name}</span>
                    ) : (
                      <span className="shrink-0 text-[10px] uppercase tracking-wider text-ivory/40 border border-ivory/15 rounded-full px-2.5 py-0.5">Anonyme</span>
                    )}
                    {v.ip && (
                      <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-mono text-ivory/60 bg-ink border border-ivory/15 rounded-full px-2.5 py-0.5"
                        data-testid={`live-visitor-ip-${i}`}>
                        <Globe className="w-3 h-3 text-gold/60" />{v.ip}
                      </span>
                    )}
                    <ExpandableText text={v.path} testId={`live-visitor-path-${i}`}
                      className="min-w-0 max-w-full text-ivory/85 font-mono text-xs bg-ink border border-ivory/10 rounded px-2.5 py-1" />
                  </span>
                  <span className="text-right shrink-0">
                    <span className="block text-ivory/55 text-xs">{when(v.seconds_ago)}</span>
                    {(v.history?.length > 0) && (
                      <span className="block text-ivory/40 text-[10px]">{v.history.length} page{v.history.length > 1 ? "s" : ""} visitée{v.history.length > 1 ? "s" : ""}</span>
                    )}
                  </span>
                </button>
                {open && (
                  <div className="mt-2 ml-7 border-l-2 border-gold/25 pl-4 py-1 space-y-1.5" data-testid={`live-visitor-history-${i}`}>
                    <p className="text-[10px] uppercase tracking-widest text-gold/80">Parcours de la visite</p>
                    {(v.history || []).length === 0 ? (
                      <p className="text-xs text-ivory/50">Page actuelle seulement (parcours non enregistré avant la mise à jour).</p>
                    ) : (
                      <ol className="space-y-1">
                        {v.history.map((h, j) => (
                          <li key={j} className="flex items-baseline gap-2.5 text-xs" data-testid={`live-visitor-history-${i}-${j}`}>
                            <span className="text-ivory/45 font-mono shrink-0 whitespace-nowrap">{hhmm(h.at)}</span>
                            <ExpandableText text={h.path} testId={`live-visitor-history-path-${i}-${j}`}
                              className={`min-w-0 font-mono ${j === v.history.length - 1 ? "text-gold" : "text-ivory/75"}`} />
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Graphique journalier des pages vues */}
      <div className="mt-7 pt-6 border-t border-ivory/10" data-testid="visitors-daily">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <h4 className="text-xs uppercase tracking-widest text-ivory/70 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-gold" /> Pages vues par jour
          </h4>
          <span className="text-xs text-ivory/60" data-testid="visitors-today">
            {periodTitle} : <strong className="text-gold">{periodViews}</strong>
          </span>
          <span className="text-xs text-ivory/50">— les mêmes pages vues qu'en haut, réparties par jour (heure de Montréal)</span>
          <div className="ml-auto flex flex-wrap gap-1.5" data-testid="visitors-range-picker">
            {RANGES.map(([d, o, lab]) => (
              <button
                key={`${d}-${o}`}
                data-testid={o ? "visitors-range-hier" : `visitors-range-${d}`}
                onClick={() => { setRange(d); setOffset(o); }}
                className={`px-3.5 py-1.5 rounded-full border text-xs transition-colors duration-300 ${
                  range === d && offset === o
                    ? "bg-gold text-ink border-gold font-bold"
                    : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"
                }`}
              >
                {lab}
              </button>
            ))}
          </div>
        </div>

        {daily === null ? (
          <div className="h-44 flex items-center justify-center text-ivory/40 text-xs">Chargement du graphique…</div>
        ) : daily === false || chart.length === 0 ? (
          <p className="text-xs text-ivory/65" data-testid="visitors-chart-empty">
            Aucune visite enregistrée pour l'instant — les statistiques s'accumulent à partir d'aujourd'hui.
          </p>
        ) : (
          <div className="h-48" data-testid="visitors-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ top: 4, right: 4, bottom: 0, left: -22 }} barCategoryGap="22%">
                <CartesianGrid vertical={false} stroke="rgba(244,241,234,0.07)" />
                <XAxis
                  dataKey="label"
                  interval={range === 1 ? 0 : tickEvery - 1}
                  tick={{ fill: "rgba(244,241,234,0.55)", fontSize: 10 }}
                  axisLine={{ stroke: "rgba(244,241,234,0.15)" }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: "rgba(244,241,234,0.55)", fontSize: 10 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: "rgba(212,175,55,0.08)" }}
                  contentStyle={{
                    background: "#14120d", border: "1px solid rgba(212,175,55,0.35)",
                    borderRadius: 12, fontSize: 12, color: "#f4f1ea",
                  }}
                  labelStyle={{ color: "#d4af37", marginBottom: 2 }}
                  formatter={(v) => [`${v} page${v > 1 ? "s" : ""} vue${v > 1 ? "s" : ""}`, ""]}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.day || ""}
                />
                <Bar dataKey="vues" fill="#d4af37" radius={[4, 4, 0, 0]} maxBarSize={26} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="text-[11px] text-ivory/55 mt-3">
          Compteur remis à zéro chaque nouvelle journée · les journées précédentes sont conservées.
        </p>

        {/* Classement « Pages chaudes » : pages les plus visitées — période INDÉPENDANTE
            du graphique (changer un bouton n'ajuste que sa section) */}
        {hotPages && hotPages.length > 0 && (
          <div className="mt-6" data-testid="hot-pages">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <p className="text-[11px] uppercase tracking-widest text-gold">
                Pages chaudes — {periodLabel(hotRange, hotOffset)}
              </p>
              <span className="text-xs text-ivory/50">pages les plus consultées sur la période — chaque affichage compte</span>
              <div className="ml-auto flex flex-wrap items-center gap-1.5" data-testid="hot-pages-range-picker">
                {RANGES.map(([d, o, lab]) => (
                  <button
                    key={`${d}-${o}`}
                    data-testid={o ? "hot-pages-range-hier" : `hot-pages-range-${d}`}
                    onClick={() => { setHotRange(d); setHotOffset(o); }}
                    className={`px-3 py-1 rounded-full border text-[11px] transition-colors duration-300 ${
                      hotRange === d && hotOffset === o
                        ? "bg-gold text-ink border-gold font-bold"
                        : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"
                    }`}
                  >
                    {lab}
                  </button>
                ))}
                {hotEmail !== null && (
                  <button
                    data-testid="hot-pages-daily-toggle"
                    onClick={toggleHotEmail}
                    title={hotEmail ? "Le classement des pages d'hier arrive chaque matin vers 6 h — cliquer pour désactiver" : "Recevoir le classement des pages d'hier par courriel chaque matin vers 6 h"}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[11px] transition-colors duration-300 ${
                      hotEmail
                        ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-400"
                        : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"
                    }`}
                  >
                    <BellRing className="w-3 h-3" />
                    {hotEmail ? "Courriel quotidien : activé" : "Courriel quotidien"}
                  </button>
                )}
              </div>
            </div>
            <ol className="space-y-2">
              {hotPages.map((p, idx) => {
                const maxN = hotPages[0].n || 1;
                return (
                  <li key={p.path} className="relative" data-testid={`hot-page-${idx}`}>
                    <div className="absolute inset-y-0 left-0 rounded-md bg-gold/10"
                      style={{ width: `${Math.max(8, (p.n / maxN) * 100)}%` }} />
                    <div className="relative flex items-center gap-3 px-3 py-1.5">
                      <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border ${
                        idx === 0 ? "bg-gold text-ink border-gold"
                        : idx < 3 ? "border-gold/50 text-gold"
                        : "border-ivory/20 text-ivory/50"}`}
                        data-testid={`hot-page-rank-${idx}`}>
                        {idx + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <ExpandableText text={p.label || p.path} testId={`hot-page-label-${idx}`}
                          className="block text-sm text-ivory/90" />
                        {p.label && p.label !== p.path && (
                          <ExpandableText text={p.path} testId={`hot-page-path-${idx}`}
                            className="block text-[10px] text-ivory/40 font-mono" />
                        )}
                      </span>
                      <span className="shrink-0 text-sm text-gold font-semibold" data-testid={`hot-page-n-${idx}`}>
                        {p.n} <span className="text-[10px] text-ivory/50 font-normal">vue{p.n > 1 ? "s" : ""}</span>
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </div>

      {/* Historique des visiteurs : nom/IP et pages parcourues, pour la période choisie */}
      <div className="mt-7 pt-6 border-t border-ivory/10" data-testid="visitors-history">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <p className="text-[11px] uppercase tracking-widest text-gold">
            Historique des visiteurs — {periodLabel(histRange, histOffset)}
          </p>
          <span className="text-xs text-ivory/50">une ligne par adresse IP ayant visité le site, avec sa ville approximative — cliquez sur une IP pour voir les pages qu'elle a visitées · 25 adresses à la fois (bouton « Afficher plus ») · bouton « Robot » pour classer une IP manuellement · l'IP est enregistrée depuis le 9 sept. 2026</span>
          <div className="ml-auto flex flex-wrap gap-1.5" data-testid="history-range-picker">
            {RANGES.map(([d, o, lab]) => (
              <button
                key={`${d}-${o}`}
                data-testid={o ? "history-range-hier" : `history-range-${d}`}
                onClick={() => { setHistRange(d); setHistOffset(o); }}
                className={`px-3 py-1 rounded-full border text-[11px] transition-colors duration-300 ${
                  histRange === d && histOffset === o
                    ? "bg-gold text-ink border-gold font-bold"
                    : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"
                }`}
              >
                {lab}
              </button>
            ))}
          </div>
        </div>

        {/* Suivi des clics « Soumission en 30 secondes » par endroit, 30 derniers
            jours (demande du propriétaire, 2026-10-17) — quelle page convertit le mieux */}
        {isAdmin && clics && clics.total > 0 && (
          <div className="mb-4 rounded-lg border border-gold/20 bg-gold/[0.03] px-3 py-2.5" data-testid="clics-soumission">
            <p className="text-[10px] uppercase tracking-wider text-gold/90 mb-2 flex items-center gap-1.5">
              <Zap className="w-3 h-3" /> Clics « Soumission 30 s » — 30 derniers jours ({clics.total} clics · {clics.total_soumissions} soum.)
            </p>
            <div className="flex flex-wrap gap-1.5" data-testid="clics-soumission-liste">
              {clics.par_source.map((c) => (
                <span key={c.source}
                  data-testid={`clics-${c.source}`}
                  className="inline-flex items-center gap-1.5 text-[10px] text-ivory/70 bg-ink border border-ivory/15 rounded-full px-2.5 py-1">
                  {{ accueil: "Accueil", catalogues: "Catalogues", forfaits: "Forfaits",
                     fiche: "Fiches produits", blogue: "Blogue", pastille: "Pastille flottante",
                     boutique: "Boutique (commande)" }[c.source] || c.source}
                  <span className="font-bold text-gold">{c.clics} clic{c.clics > 1 ? "s" : ""}</span>
                  <span className="text-ivory/40">·</span>
                  <span className="font-bold text-emerald-400">{c.soumissions} soum.</span>
                  {c.clics > 0 && (
                    <span className="text-ivory/50">({Math.round(100 * c.soumissions / c.clics)} %)</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Liste des IP bloquées du site complet (propriétaire seul) — chaque ligne
            permet de nommer/modifier le nom et de débloquer l'adresse */}
        {isAdmin && blockedList !== null && (
          <div className="mb-4 rounded-lg border border-red-500/25 bg-red-500/[0.04] px-3 py-2" data-testid="visitors-blocked">
            <div className="w-full flex items-center gap-2.5">
              <button type="button" onClick={() => setBlockedOpen((o) => !o)}
                data-testid="visitors-blocked-toggle"
                className="flex items-center gap-2.5 text-left group">
                {blockedOpen
                  ? <ChevronDown className="w-3.5 h-3.5 text-red-400 shrink-0" />
                  : <ChevronRight className="w-3.5 h-3.5 text-ivory/40 group-hover:text-red-400 shrink-0 transition-colors" />}
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-red-300 border border-red-500/40 rounded-full px-2.5 py-0.5">
                  <Ban className="w-3 h-3" /> IP bloquées
                </span>
                <span className="text-ivory/55 text-xs" data-testid="visitors-blocked-count">
                  {blockedList.length === 0
                    ? "aucune adresse bloquée"
                    : `${blockedList.length} adresse${blockedList.length > 1 ? "s" : ""} sans accès au site`}
                </span>
              </button>
              {/* Rapport hebdo du lundi (blocages automatiques) — test d'envoi immédiat */}
              <button type="button"
                onClick={async () => {
                  try {
                    const r = await api.post("/admin/visiteurs/blocages/rapport", { envoyer: true });
                    r.data.n ? toast.success(r.data.message) : toast.info(r.data.message);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Envoi impossible — réessayez.");
                  }
                }}
                data-testid="visitors-blocked-rapport"
                title="Le rapport des blocages automatiques part chaque lundi — cliquer pour l'envoyer maintenant"
                className="ml-auto shrink-0 inline-flex items-center gap-1 rounded-full border border-ivory/20 text-ivory/60 px-2 py-1 text-[10px] hover:border-gold/50 hover:text-gold transition-colors duration-300">
                <BellRing className="w-3 h-3" /> Rapport du lundi
              </button>
            </div>
            {blockedOpen && (
              blockedList.length === 0 ? (
                <p className="mt-2 text-xs text-ivory/50" data-testid="visitors-blocked-empty">
                  Aucune adresse IP bloquée — utilisez le bouton « Bloquer » d'un visiteur ci-dessous.
                </p>
              ) : (
                <ul className="mt-2 space-y-2" data-testid="visitors-blocked-list">
                  {blockedList.map((b) => (
                    <li key={b.ip} className="border-b border-ivory/5 pb-2 last:border-0 last:pb-0" data-testid={`blocked-ip-${b.ip}`}>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-mono text-ivory/70 bg-ink border border-ivory/15 rounded-full px-2.5 py-0.5"
                          data-testid={`blocked-ip-addr-${b.ip}`}>
                          <Globe className="w-3 h-3 text-red-400/70" />{b.ip}
                        </span>
                        {b.nom && (
                          <span className="shrink-0 text-[10px] font-semibold text-champagne bg-champagne/10 border border-champagne/40 rounded-full px-2 py-0.5"
                            data-testid={`blocked-ip-nom-${b.ip}`}>{b.nom}</span>
                        )}
                        {b.auto && (
                          <span className="shrink-0 text-[10px] font-semibold text-orange-300 bg-orange-400/10 border border-orange-400/40 rounded-full px-2 py-0.5"
                            title={b.motif || "Blocage automatique anti-marteau"}
                            data-testid={`blocked-ip-auto-${b.ip}`}>
                            auto{b.motif ? ` · ${b.motif}` : ""}
                          </span>
                        )}
                        <span className="text-[10px] text-ivory/45" data-testid={`blocked-ip-detail-${b.ip}`}>
                          bloquée {dtshort(b.at)}{b.par && b.par !== "robot-auto" ? ` par ${b.par}` : ""}
                          {" · "}{b.expire ? `se lève le ${dtshort(b.expire)}` : "toujours"}
                        </span>
                        <span className="ml-auto flex items-center gap-1.5">
                          <button type="button"
                            onClick={() => { setEditingIp(editingIp === b.ip ? null : b.ip); setEditingVal(ipNames[b.ip] || b.nom || ""); }}
                            data-testid={`blocked-ip-name-edit-${b.ip}`}
                            title={b.nom ? `Modifier le nom « ${b.nom} »` : "Donner un nom à cette IP"}
                            className="inline-flex items-center gap-1 rounded-full border border-champagne/40 text-champagne/90 px-2 py-1 text-[10px] hover:bg-champagne/10 transition-colors duration-300">
                            <Pencil className="w-3 h-3" />
                            {b.nom ? "Nom" : "Nommer"}
                          </button>
                          <button type="button"
                            onClick={() => toggleBlock(b.ip, false)}
                            data-testid={`blocked-ip-unblock-${b.ip}`}
                            title="Débloquer cette adresse IP — l'accès au site sera rétabli"
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-500/50 text-emerald-400 px-2 py-1 text-[10px] hover:bg-emerald-500/10 transition-colors duration-300">
                            <Unlock className="w-3 h-3" /> Débloquer
                          </button>
                        </span>
                      </div>
                      {editingIp === b.ip && (
                        <div className="mt-2 flex items-center gap-2" data-testid={`blocked-ip-name-form-${b.ip}`}>
                          <input
                            autoFocus
                            value={editingVal}
                            onChange={(e) => setEditingVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveNom(b.ip); if (e.key === "Escape") setEditingIp(null); }}
                            maxLength={60}
                            placeholder="Nom de cette IP — vide pour retirer"
                            data-testid={`blocked-ip-name-input-${b.ip}`}
                            className="w-72 max-w-full bg-ink border border-champagne/40 rounded-lg px-3 py-1.5 text-xs text-ivory placeholder:text-ivory/35 focus:outline-none focus:border-champagne"
                          />
                          <button type="button" onClick={() => saveNom(b.ip)}
                            data-testid={`blocked-ip-name-save-${b.ip}`}
                            className="inline-flex items-center gap-1 rounded-full border border-emerald-500/50 text-emerald-400 px-2.5 py-1 text-[10px] hover:bg-emerald-500/10 transition-colors">
                            <Check className="w-3 h-3" /> OK
                          </button>
                          <button type="button" onClick={() => setEditingIp(null)}
                            data-testid={`blocked-ip-name-cancel-${b.ip}`}
                            className="inline-flex items-center gap-1 rounded-full border border-ivory/20 text-ivory/60 px-2.5 py-1 text-[10px] hover:border-ivory/40 transition-colors">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )
            )}
            {/* Journal des blocages AUTOMATIQUES (90 derniers jours) — liste
                déroulante chargée à la demande (demande du propriétaire, 2026-10-17) */}
            <div className="mt-2 pt-2 border-t border-ivory/10">
              <button type="button"
                onClick={async () => {
                  const ouvrir = !journalOpen;
                  setJournalOpen(ouvrir);
                  if (ouvrir && journal === null) {
                    try {
                      const r = await api.get("/admin/visiteurs/blocages/journal");
                      setJournal(r.data.evenements || []);
                    } catch { setJournal([]); }
                  }
                }}
                data-testid="visitors-journal-toggle"
                className="flex items-center gap-2.5 text-left group">
                {journalOpen
                  ? <ChevronDown className="w-3.5 h-3.5 text-orange-300 shrink-0" />
                  : <ChevronRight className="w-3.5 h-3.5 text-ivory/40 group-hover:text-orange-300 shrink-0 transition-colors" />}
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-orange-300 border border-orange-400/40 rounded-full px-2.5 py-0.5">
                  <Bot className="w-3 h-3" /> Journal des blocages automatiques
                </span>
                <span className="text-ivory/45 text-xs">90 derniers jours</span>
              </button>
              {journalOpen && (
                journal === null ? (
                  <p className="mt-2 text-xs text-ivory/50">Chargement…</p>
                ) : journal.length === 0 ? (
                  <p className="mt-2 text-xs text-ivory/50" data-testid="visitors-journal-empty">
                    Aucun blocage automatique dans les 90 derniers jours.
                  </p>
                ) : (
                  <ul className="mt-2 space-y-1.5" data-testid="visitors-journal-list">
                    {journal.map((e, i) => (
                      <li key={`${e.ip}-${e.at}-${i}`} className="flex items-center gap-2.5 flex-wrap text-[11px]"
                        data-testid={`journal-entry-${e.ip}-${i}`}>
                        <span className="text-ivory/55 shrink-0">{dtshort(e.at)}</span>
                        <span className="shrink-0 inline-flex items-center gap-1 font-mono text-[10px] text-ivory/70 bg-ink border border-ivory/15 rounded-full px-2 py-0.5">
                          <Globe className="w-3 h-3 text-orange-300/70" />{e.ip}
                        </span>
                        {e.org && <span className="text-ivory/50">{e.org}</span>}
                        <span className="text-ivory/45">{e.n} req/min</span>
                        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${
                          e.en_cours
                            ? "border-red-500/50 text-red-300"
                            : "border-ivory/15 text-ivory/45"}`}
                          data-testid={`journal-statut-${e.ip}-${i}`}>
                          {e.en_cours ? "blocage en vigueur" : "blocage levé"}
                        </span>
                        {/* Déblocage direct depuis le journal (entrées encore en vigueur) */}
                        {e.en_cours && (
                          <button type="button"
                            onClick={async () => {
                              await toggleBlock(e.ip, false);
                              setJournal((j) => j ? j.map((x, xi) => (xi === i ? { ...x, en_cours: false } : x)) : j);
                            }}
                            data-testid={`journal-unblock-${e.ip}-${i}`}
                            title="Débloquer cette adresse IP — l'accès au site sera rétabli"
                            className="shrink-0 inline-flex items-center gap-1 rounded-full border border-emerald-500/50 text-emerald-400 px-2 py-0.5 text-[10px] hover:bg-emerald-500/10 transition-colors duration-300">
                            <Unlock className="w-3 h-3" /> Débloquer
                          </button>
                        )}
                        <span className="ml-auto" />
                      </li>
                    ))}
                  </ul>
                )
              )}
            </div>
          </div>
        )}
        {hist === null ? (
          <p className="text-xs text-ivory/50">Chargement…</p>
        ) : hist.length === 0 && histBotsList.length === 0 ? (
          <p className="text-xs text-ivory/50" data-testid="visitors-history-empty">
            Aucun visiteur enregistré pour cette période — l'historique détaillé (nom/IP) s'accumule à partir de maintenant.
          </p>
        ) : (
          (() => {
            // Une ligne par adresse IP (vrais visiteurs, puis section robots à part) —
            // déplier montre les pages visitées. testidPrefix distingue les deux listes.
            const renderIpRow = (v, testidPrefix) => {
              const key = `${testidPrefix}-${v.ip}`;
              const open = histOpen === key;
              const org = histGeo[v.ip]?.org || "";
              const isBotRow = testidPrefix === "robot";
              return (
                <li key={key} data-testid={`visitor-history-${key}`}
                  className="text-sm border-b border-ivory/5 pb-2 last:border-0 last:pb-0">
                  {/* Réorganisation petits écrans (demande du propriétaire, 2026-10-23) :
                      sur mobile la fiche visiteur s'empile en colonnes lisibles —
                      identité d'abord, date en dessous, actions sur leur propre ligne ;
                      sur grand écran, la rangée compacte d'avant est conservée */}
                  <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-1.5">
                  <button type="button" onClick={() => setHistOpen(open ? null : key)}
                    data-testid={`visitor-history-toggle-${key}`}
                    className="w-full sm:w-auto sm:flex-1 min-w-0 flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3 text-left group">
                    <span className="flex items-center gap-2.5 min-w-0 flex-wrap">
                      {open
                        ? <ChevronDown className="w-3.5 h-3.5 text-gold shrink-0" />
                        : <ChevronRight className="w-3.5 h-3.5 text-ivory/40 group-hover:text-gold shrink-0 transition-colors" />}
                      <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-mono text-ivory/70 bg-ink border border-ivory/15 rounded-full px-2.5 py-0.5"
                        data-testid={`visitor-history-ip-${key}`}>
                        <Globe className="w-3 h-3 text-gold/60" />{v.ip}
                      </span>
                      {ipNames[v.ip] && (
                        <span className="shrink-0 text-[10px] font-semibold text-champagne bg-champagne/10 border border-champagne/40 rounded-full px-2 py-0.5"
                          data-testid={`visitor-history-customname-${key}`}>{ipNames[v.ip]}</span>
                      )}
                      {org && (
                        <span className="shrink-0 text-[10px] font-semibold text-amber-400/90 bg-amber-400/10 border border-amber-400/30 rounded-full px-2 py-0.5"
                          data-testid={`visitor-history-org-${key}`}>{org}</span>
                      )}
                      {histGeo[v.ip]?.label && (
                        <span className="shrink-0 text-[10px] text-champagne/90"
                          data-testid={`visitor-history-city-${key}`}>{histGeo[v.ip].label}</span>
                      )}
                      {/* Position GPS exacte CONSENTIE par le visiteur (permission du
                          navigateur — demande du propriétaire 2026-10-10) */}
                      {v.geo?.lat != null && (
                        <a href={`https://www.google.com/maps?q=${v.geo.lat},${v.geo.lng}`}
                          target="_blank" rel="noreferrer"
                          data-testid={`visitor-history-gps-${key}`}
                          title="Position GPS exacte consentie par ce visiteur — ouvre Google Maps"
                          className="shrink-0 inline-flex items-center gap-1 text-[10px] text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2 py-0.5 hover:bg-emerald-400/20 transition-colors">
                          <MapPin className="w-3 h-3" /> GPS {Number(v.geo.lat).toFixed(4)}, {Number(v.geo.lng).toFixed(4)}
                        </a>
                      )}
                      {(v.names || []).map((n) => (
                        <span key={n} className="shrink-0 text-xs font-semibold text-gold bg-gold/10 border border-gold/30 rounded-full px-2.5 py-0.5"
                          data-testid={`visitor-history-name-${key}-${n}`}>{n}</span>
                      ))}
                      <span className="text-ivory/55 text-xs">{v.views} page{v.views > 1 ? "s" : ""}</span>
                    </span>
                    <span className="self-start sm:self-auto pl-6 sm:pl-0 text-left sm:text-right shrink-0 text-ivory/55 text-xs">{dtshort(v.last)}</span>
                  </button>
                  {/* Actions (Nommer / Robot / Bloquer) : leur propre ligne sur mobile */}
                  <div className="flex items-center gap-1.5 flex-wrap pl-6 sm:pl-0 self-start sm:self-auto">
                  {/* Nom personnalisé sur l'IP (modifiable — 2026-10-17) */}
                  <button type="button"
                    onClick={() => { setEditingIp(editingIp === v.ip ? null : v.ip); setEditingVal(ipNames[v.ip] || ""); }}
                    data-testid={`visitor-history-name-edit-${key}`}
                    title={ipNames[v.ip] ? `Modifier le nom « ${ipNames[v.ip]} » de cette IP` : "Donner un nom à cette adresse IP (ex. Fournisseur X)"}
                    className="shrink-0 mt-0.5 inline-flex items-center gap-1 rounded-full border border-champagne/40 text-champagne/90 px-2 py-1 text-[10px] hover:bg-champagne/10 transition-colors duration-300">
                    <Pencil className="w-3 h-3" />
                    {ipNames[v.ip] ? "Nom" : "Nommer"}
                  </button>
                  <button type="button"
                    onClick={() => markBot(v.ip, !isBotRow)}
                    data-testid={`visitor-history-${isBotRow ? "unmark" : "mark"}-bot-${key}`}
                    title={isBotRow
                      ? "Replacer cette IP parmi les visiteurs (marquage manuel)"
                      : "Marquer cette IP comme robot — exclue de toutes les statistiques (marquage manuel)"}
                    className={`shrink-0 mt-0.5 inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] transition-colors duration-300 ${
                      isBotRow
                        ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                        : "border-amber-400/40 text-amber-400/90 hover:bg-amber-400/10"
                    }`}>
                    {isBotRow ? <Undo2 className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
                    {isBotRow ? "Visiteur" : "Robot"}
                  </button>
                  {/* Blocage site complet (propriétaire seul — 2026-10-16) ;
                      clic = choix de durée 24 h / 7 j / toujours (2026-10-17) */}
                  {isAdmin && (
                    <button type="button"
                      onClick={() => blockedIps.has(v.ip)
                        ? toggleBlock(v.ip, false)
                        : setBlockMenuIp(blockMenuIp === v.ip ? null : v.ip)}
                      data-testid={`visitor-history-block-${key}`}
                      title={blockedIps.has(v.ip)
                        ? "Débloquer cette adresse IP — l'accès au site sera rétabli"
                        : "Bloquer cette adresse IP du site COMPLET — choisir la durée"}
                      className={`shrink-0 mt-0.5 inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] transition-colors duration-300 ${
                        blockedIps.has(v.ip)
                          ? "border-red-500/60 bg-red-500/15 text-red-300 hover:bg-red-500/25"
                          : "border-red-500/40 text-red-400/80 hover:bg-red-500/10"
                      }`}>
                      {blockedIps.has(v.ip) ? <Unlock className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                      {blockedIps.has(v.ip) ? "Bloquée" : "Bloquer"}
                    </button>
                  )}
                  </div>
                  </div>
                  {editingIp === v.ip && (
                    <div className="mt-2 ml-7 flex items-center gap-2" data-testid={`visitor-history-name-form-${key}`}>
                      <input
                        autoFocus
                        value={editingVal}
                        onChange={(e) => setEditingVal(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveNom(v.ip); if (e.key === "Escape") setEditingIp(null); }}
                        maxLength={60}
                        placeholder="Nom de cette IP (ex. Fournisseur X) — vide pour retirer"
                        data-testid={`visitor-history-name-input-${key}`}
                        className="w-72 max-w-full bg-ink border border-champagne/40 rounded-lg px-3 py-1.5 text-xs text-ivory placeholder:text-ivory/35 focus:outline-none focus:border-champagne"
                      />
                      <button type="button" onClick={() => saveNom(v.ip)}
                        data-testid={`visitor-history-name-save-${key}`}
                        title="Enregistrer le nom"
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-500/50 text-emerald-400 px-2.5 py-1 text-[10px] hover:bg-emerald-500/10 transition-colors">
                        <Check className="w-3 h-3" /> OK
                      </button>
                      <button type="button" onClick={() => setEditingIp(null)}
                        data-testid={`visitor-history-name-cancel-${key}`}
                        title="Annuler"
                        className="inline-flex items-center gap-1 rounded-full border border-ivory/20 text-ivory/60 px-2.5 py-1 text-[10px] hover:border-ivory/40 transition-colors">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {blockMenuIp === v.ip && !blockedIps.has(v.ip) && (
                    <div className="mt-2 ml-7 flex items-center gap-2 flex-wrap" data-testid={`visitor-history-blockmenu-${key}`}>
                      <span className="text-[10px] uppercase tracking-widest text-red-300/90">Bloquer {v.ip} pour :</span>
                      {[["24h", "24 heures"], ["7j", "7 jours"], ["toujours", "Toujours"]].map(([d, lab]) => (
                        <button key={d} type="button"
                          onClick={() => toggleBlock(v.ip, true, d)}
                          data-testid={`visitor-history-block-${d}-${key}`}
                          title={d === "toujours"
                            ? "Blocage permanent — à lever à la main dans la liste des IP bloquées"
                            : `Le blocage se lèvera tout seul après ${lab.toLowerCase()}`}
                          className="inline-flex items-center gap-1 rounded-full border border-red-500/50 text-red-300 px-3 py-1 text-[10px] hover:bg-red-500/15 transition-colors duration-300">
                          <Ban className="w-3 h-3" /> {lab}
                        </button>
                      ))}
                      <button type="button" onClick={() => setBlockMenuIp(null)}
                        data-testid={`visitor-history-block-cancel-${key}`}
                        title="Annuler"
                        className="inline-flex items-center gap-1 rounded-full border border-ivory/20 text-ivory/60 px-2.5 py-1 text-[10px] hover:border-ivory/40 transition-colors">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  {open && (
                    <div className="mt-2 ml-7 border-l-2 border-gold/25 pl-4 py-1 space-y-1.5" data-testid={`visitor-history-journey-${key}`}>
                      <p className="text-[10px] uppercase tracking-widest text-gold/80">
                        Pages visitées depuis {v.ip}{histGeo[v.ip]?.label ? ` (${histGeo[v.ip].label})` : ""}
                      </p>
                      <ol className="space-y-1">
                        {(v.journey || []).map((h, j) => (
                          <li key={j} className="flex items-baseline gap-2.5 text-xs" data-testid={`visitor-history-journey-${key}-${j}`}>
                            <span className="text-ivory/45 font-mono shrink-0 whitespace-nowrap">{hhmm(h.ts)}</span>
                            <ExpandableText text={h.path} testId={`visitor-history-path-${key}-${j}`}
                              className={`min-w-0 font-mono ${j === v.journey.length - 1 ? "text-gold" : "text-ivory/75"}`} />
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </li>
              );
            };
            return (
              <>
                {hist.length === 0 && (
                  <p className="text-xs text-ivory/50 mb-2" data-testid="visitors-history-only-bots">
                    Aucun vrai visiteur sur la période — seuls des robots sont passés (voir ci-dessous).
                  </p>
                )}
                <ul className="space-y-2">
                  {hist.map((v) => renderIpRow(v, "ip"))}
                </ul>
                <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="visitors-history-pagination">
                  {histHasMore && (
                    <button type="button" data-testid="visitors-history-more"
                      disabled={histLoadingMore}
                      onClick={() => loadHist(histSkip + 25, true)}
                      className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60">
                      {histLoadingMore ? "Chargement…" : "Afficher 25 de plus"}
                    </button>
                  )}
                  {histTotal > 0 && (
                    <span className="text-[11px] text-ivory/50" data-testid="visitors-history-count">
                      {hist.length} adresse{hist.length > 1 ? "s" : ""} affichée{hist.length > 1 ? "s" : ""} sur {histTotal}
                    </span>
                  )}
                </div>
                {histBotsList.length > 0 && (
                  <div className="mt-3 border-t border-ivory/10 pt-3" data-testid="visitors-bots">
                    <button type="button" onClick={() => setHistBotsOpen((o) => !o)}
                      data-testid="visitors-bots-toggle"
                      className="w-full flex items-center gap-2.5 text-left group">
                      {histBotsOpen
                        ? <ChevronDown className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        : <ChevronRight className="w-3.5 h-3.5 text-ivory/40 group-hover:text-amber-400 shrink-0 transition-colors" />}
                      <span className="text-[10px] uppercase tracking-wider text-amber-400/90 border border-amber-400/30 rounded-full px-2.5 py-0.5">
                        IP robots
                      </span>
                      <span className="text-ivory/55 text-xs">
                        {histBots} robot{histBots > 1 ? "s" : ""} identifié{histBots > 1 ? "s" : ""} (Google, Bing…) — masqué{histBots > 1 ? "s" : ""} de la liste des visiteurs
                      </span>
                    </button>
                    {histBotsOpen && (
                      <ul className="mt-2 space-y-2" data-testid="visitors-bots-list">
                        {histBotsList.map((v) => renderIpRow(v, "robot"))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            );
          })()
        )}
      </div>

      <p className="text-[11px] text-ivory/55 mt-4">Rafraîchi aux 30 s · l'historique est une liste d'adresses IP : cliquez une IP pour voir les pages visitées depuis celle-ci, avec sa ville approximative · réservé à l'admin.</p>
    </div>
  );
}
